import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { minLength, dobIsValid, isAddressIncomplete, isRetailPinValid } from 'utils/validators'

import { H2, H4, fontWeights, lineHeights } from 'styles/typography'
import { media, mediaDown } from 'styles/media'
import { black } from 'styles/colors'
import * as spacing from 'styles/spacing'

import { ModalSection } from 'components/Modal'
import Input from 'components/Input'
import Tooltip from 'components/Tooltip'
import NumberInput from 'components/NumberInput'
import AddressComplete from 'components/AddressComplete'
import ManualAddressFieldset from 'components/ManualAddressFieldset'
import Button from 'components/button/Button'
import Checkbox from 'components/Checkbox'
import Fieldset from 'components/Fieldset/index'
import FieldsetItem from 'components/Fieldset/FieldsetItem'
import FormFeedback from 'components/FormFeedback'
import NativeAuthContext from 'components/NativeAuth'
import SignUpBanner from 'components/SignUpBanner'
import Welcome from 'components/account/Welcome'

const Container = styled.div`
  text-align: left;
`

const Action = styled.button`
  color: ${black};
  text-decoration: underline;
  font-weight: ${fontWeights.medium};
`

const Heading = styled(H2)`
  text-transform: uppercase;
  font-family: 'Trim Poster';
  text-align: center;
`

const Subheading = styled(H4)`
  font-weight: ${fontWeights.regular};
  line-height: ${lineHeights.base};
  text-align: center;
`

const Footer = styled.div`
  position: static;
  text-align: center;

  ${mediaDown.medium`
    display: flex;
    align-items: center;
    flex-direction: column-reverse;
  `};

  ${media.medium`
    display: grid;
    align-items: center;
    grid-template-columns: 120px 1fr 120px;
  `};
`

const BackWrap = styled.div`
  margin-top: ${spacing.medium};

  ${media.medium`
    margin-top: 0;
    display: flex;
  `};
`

const Back = styled.button`
  color: ${black};
  font-weight: ${fontWeights.medium};
  text-decoration: underline;
`

const StyledButton = styled.button`
  text-decoration: underline;
`

const validationSchema = () => {
  return Yup.lazy(values => {
    let schema = {
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Email address is invalid').required('Email address is required'),
      dob: Yup.string().required('Date of birth is required').test('dob', 'Birth of date is not valid', dobIsValid()),
      password: Yup.string()
        .required('Password is required')
        .test('password', 'Must be at least 8 characters', minLength(8)),
      retailPin: Yup.string().test('retailPin', 'Retail pin is invalid', isRetailPinValid(values))
    }

    if (!values.manualEntry) {
      schema = {
        ...schema,
        address: Yup.string()
          .required('Address is required')
          .test('address', 'You must select an address in the suggested list', isAddressIncomplete(values))
      }
    } else {
      schema = {
        ...schema,
        street: Yup.string().required('Street is required')
      }
    }

    return Yup.object().shape(schema)
  })
}

const SignupForm = ({
  closeModal,
  utmCampaign,
  utmSource,
  utmMedium,
  setBoostStep,
  visible,
  isBoost,
  agentSource,
  queryParams = {}
}) => {
  const { user, signupError, signup, setLoginModalVisibility, validateEmail } = useContext(NativeAuthContext)
  const [showRetailPin, setShowRetailPin] = useState(false)
  const [step, setStep] = useState('email')

  const goToLogin = () => {
    closeModal()
    setLoginModalVisibility(true)
  }

  useEffect(() => {
    setShowRetailPin(
      window.location.pathname === '/ontario' ||
        window.location.pathname === '/bc' ||
        (!user && JSON.parse(localStorage.getItem('softVerifiedUser'))?.province === 'ON') ||
        (!user && JSON.parse(localStorage.getItem('softVerifiedUser'))?.province === 'BC')
    )

    return () => {
      // resetForm()
    }
  }, [])

  useEffect(() => {
    if (!visible) {
      // resetForm()
      // setStatus(null)
      setStep('email')
    }
  }, [visible])

  const handleNextClick = ({ values, setErrors, errors }) => async () => {
    const { email } = values
    const { exists } = await validateEmail({ email })

    if (exists) {
      setErrors({
        ...errors,
        email: 'Email address already exists'
      })
      return
    }

    setErrors({})
    setStep('name')
  }

  const submitForm = async (values, { setSubmitting, setFieldError, setStatus }) => {
    setSubmitting(true)

    const response = await signup({
      ...values,
      ...(agentSource ? { agentSource } : {}),
      phoneNumber: values.phoneNumber.replace(/[^0-9]+/g, '')
    })

    if (response === 'emailAlreadyExists') setFieldError('email', 'Email already exists')
    if (response === 'emailAccountInvalid') setFieldError('email', 'Email address invalid. (from BriteVerify)')
    if (response === 'unallowedProvince') setFieldError('address', 'Province not supported.')
    if (response !== 'accountInvalid') setStatus({ success: true })

    if (setBoostStep) {
      setBoostStep('orderReview')
    }

    setSubmitting(false)

    if (queryParams.token && queryParams.pixvalid) {
      try {
        await fetch(`https://api.trks.ca/vuse.php?pixvalid=${queryParams.pixvalid}&token=${queryParams.token}&t=1`)
      } catch (e) {
        console.log(e)
      }
    }
  }

  useEffect(() => {
    if (status?.success) {
      setStep('success')
    }
  }, [status])

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        passwordConfirm: '',
        dob: '',
        addressLabel: '',
        address: '',
        emailSubscription: false,
        consent: false,
        street: '',
        province: '',
        city: '',
        country: '',
        postalCode: '',
        utmCampaign,
        utmSource,
        utmMedium,
        idQRCode: queryParams.idQRCode || '',
        retailPin: ''
      }}
      validationSchema={validationSchema}
      onSubmit={submitForm}
      validateOnChange={false}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
        setFieldError,
        setErrors
      }) => (
        <Container>
          {step === 'success' ? (
            <Welcome onButtonClick={() => closeModal()} />
          ) : (
            <>
              <SignUpBanner
                text={
                  <p>
                    Hi there! For a smooth shopping experience please fill in your details as it appears on your{' '}
                    <strong>government issued ID</strong>. We at Vuse do not like filling out forms neither but we
                    require them to validate that you're over the legal age to buy vaping products. We appreciate your
                    effort!
                  </p>
                }
              />

              {step === 'email' && (
                <>
                  <ModalSection>
                    <Heading>Sign up</Heading>
                    {!isBoost && (
                      <p
                        css={`
                          text-align: center;
                        `}
                      >
                        Already have an account? <Action onClick={goToLogin}>Log in</Action>.
                      </p>
                    )}
                  </ModalSection>
                </>
              )}

              {step === 'name' && (
                <ModalSection>
                  <Subheading>Just a few more steps to get you ready for shipping</Subheading>
                </ModalSection>
              )}

              <ModalSection>
                <form onSubmit={handleSubmit}>
                  <Fieldset>
                    {step === 'email' && (
                      <>
                        <FieldsetItem xs="1-1" sm="1-2">
                          <Input
                            label="Email address"
                            name="email"
                            placeholder="john.doe@mail.com"
                            type="text"
                            value={values.email}
                            onChange={e => {
                              if (errors.email) delete errors.email
                              handleChange(e)
                            }}
                            onBlur={handleBlur}
                            meta={{
                              error: errors.email,
                              touched: touched.email
                            }}
                          />
                        </FieldsetItem>

                        <FieldsetItem xs="1-1" sm="1-2">
                          <Input
                            label="Password"
                            name="password"
                            placeholder="••••••••••"
                            type="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            meta={{
                              error: errors.password,
                              touched: touched.password
                            }}
                          />
                        </FieldsetItem>

                        <FieldsetItem xs="1-1">
                          <Footer>
                            <div>&nbsp;</div>
                            <div>
                              <Button
                                theme="custom"
                                customColor={black}
                                variant="outline"
                                size="large"
                                type="submit"
                                disabled={
                                  values.email.length === 0 ||
                                  values.password.length === 0 ||
                                  errors.email ||
                                  errors.password
                                }
                                onClick={handleNextClick({ values, setErrors, errors })}
                              >
                                Next
                              </Button>
                            </div>
                          </Footer>
                        </FieldsetItem>
                      </>
                    )}

                    {step === 'name' && (
                      <>
                        <FieldsetItem xs="1-1" sm="1-2">
                          <Input
                            label="First legal name"
                            placeholder="John"
                            name="firstName"
                            type="text"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            meta={{
                              error: errors.firstName,
                              touched: touched.firstName
                            }}
                          />
                        </FieldsetItem>

                        <FieldsetItem xs="1-1" sm="1-2">
                          <Input
                            label="Last legal name"
                            placeholder="Doe"
                            name="lastName"
                            type="text"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            meta={{
                              error: errors.lastName,
                              touched: touched.lastName
                            }}
                          />
                        </FieldsetItem>

                        {!values.manualEntry && (
                          <>
                            <FieldsetItem xs="1-1">
                              <AddressComplete
                                allowManualEntry
                                type="text"
                                label="Residential address"
                                value={values.address}
                                placeholder="Start typing..."
                                name="address"
                                onChange={handleChange}
                                setFieldValue={setFieldValue}
                                setErrors={setErrors}
                                values={values}
                                meta={{
                                  error: errors.address,
                                  touched: touched.address
                                }}
                                hint={
                                  <span>
                                    Can't find your address?&nbsp;
                                    <StyledButton
                                      type="button"
                                      onClick={() => {
                                        setFieldValue('manualEntry', true)
                                      }}
                                    >
                                      Enter it manually instead
                                    </StyledButton>
                                  </span>
                                }
                              />
                            </FieldsetItem>
                          </>
                        )}

                        {values.manualEntry && (
                          <>
                            <ManualAddressFieldset
                              setFieldError={setFieldError}
                              setFieldValue={setFieldValue}
                              values={values}
                            />
                            <FieldsetItem xs="1-1">
                              <StyledButton
                                type="button"
                                onClick={() => {
                                  setFieldValue('manualEntry', false)
                                }}
                              >
                                Autocomplete address
                              </StyledButton>
                            </FieldsetItem>
                          </>
                        )}

                        <FieldsetItem xs="1-1" sm="1-2">
                          <NumberInput
                            label="Date of birth"
                            name="dob"
                            value={values.dob}
                            contrast
                            onChange={handleChange}
                            numberFormat="####/##/##"
                            placeholder="YYYY/MM/DD"
                            meta={{
                              error: errors.dob,
                              touched: touched.dob
                            }}
                            onBlur={handleBlur}
                          />
                        </FieldsetItem>

                        {showRetailPin && (
                          <FieldsetItem xs="1-1" sm="1-2">
                            <NumberInput
                              label="Retail pin (optional)"
                              name="retailPin"
                              value={values.retailPin}
                              contrast
                              onChange={handleChange}
                              numberFormat="#####"
                              placeholder="12345"
                              meta={{
                                error: errors.retailPin,
                                touched: touched.retailPin
                              }}
                              onBlur={handleBlur}
                            />
                          </FieldsetItem>
                        )}

                        <FieldsetItem xs="1-1">
                          <Checkbox
                            label={
                              <p>
                                By checking this box, I acknowledge that I read, understood and agree to the&nbsp;
                                <a href="/pages/terms-conditions/" target="_blank">
                                  terms and conditions
                                </a>
                                .
                              </p>
                            }
                            name="consent"
                            onChange={handleChange}
                            checked={values.consent}
                          />
                        </FieldsetItem>

                        <FieldsetItem xs="1-1">
                          <Checkbox
                            label={
                              <p>
                                <span>
                                  Yes, I want to receive information about <strong>Vuse</strong> products, services and
                                  research opportunities that may be of interest. You can unsubscribe at any time.
                                </span>
                                <Tooltip
                                  content={
                                    <p>
                                      Vuse
                                      <br />
                                      Imperial Tobacco Company, 3711 Saint-Antoine St W, Montreal, QC H4C 3P6 , email:{' '}
                                      <a href="mailto:support@batsupport.zendesk.com">support@batsupport.zendesk.com</a>
                                      , tel: <a href="tel:18447468973">1-844-746-8973</a>
                                    </p>
                                  }
                                >
                                  Hover over here for our contact information. Click the checkbox to consent.
                                </Tooltip>
                                <span>
                                  This information will be used to create your account and verify your identity
                                  {isBoost && <span> and is mandatory to receive your ePod</span>}.
                                </span>
                              </p>
                            }
                            name="emailSubscription"
                            onChange={handleChange}
                            checked={values.emailSubscription}
                          />
                        </FieldsetItem>

                        <FieldsetItem>
                          <p>
                            Your personal information will be used in accordance of our{' '}
                            <a href="/pages/privacy-policy/" target="_blank">
                              Privacy Policy
                            </a>
                          </p>
                        </FieldsetItem>

                        <FieldsetItem xs="1-1">
                          <Footer>
                            <BackWrap>
                              <Back
                                type="button"
                                onClick={() => {
                                  setStep('email')
                                }}
                              >
                                Go Back
                              </Back>
                            </BackWrap>
                            <div>
                              <Button
                                theme="custom"
                                customColor={black}
                                variant="outline"
                                size="large"
                                type="submit"
                                disabled={
                                  isBoost
                                    ? !values.consent || !values.emailSubscription || isSubmitting
                                    : !values.consent || isSubmitting
                                }
                              >
                                Register
                              </Button>
                            </div>
                          </Footer>
                        </FieldsetItem>
                      </>
                    )}

                    {signupError === 'idVerification' && (
                      <FieldsetItem xs="1-1">
                        <FormFeedback type="error">
                          Oops, looks like we couldn't validate your identity with the address you provided. Have you
                          moved recently? Try entering your previous residential address. Please contact our customer
                          service by phone at 1-833-308-8873 or email us at{' '}
                          <a href="mailto:support@batsupport.zendesk.com">support@batsupport.zendesk.com</a> for further
                          assistance.
                        </FormFeedback>
                      </FieldsetItem>
                    )}

                    {signupError === 'accountInvalid' && (
                      <FieldsetItem xs="1-1">
                        <FormFeedback type="error">
                          This account can not be created. For support, please contact customer service.
                        </FormFeedback>
                      </FieldsetItem>
                    )}
                  </Fieldset>
                </form>
              </ModalSection>
            </>
          )}
        </Container>
      )}
    </Formik>
  )
}

export default SignupForm
